import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { Collections, db, functions } from '../../utils/firebase.config';
import { converterOf } from '../../utils/firebase.utils';
import EntityShopGroup from '../../entities/shop-group.entity';
import FetchError from '../../errors/fetch.error';
import CreateShopGroupInput from './resources/create-shop-group.resource';
import UpdateShopGroupInput from './resources/update-shop-group.resource';

export default function useShopGroups() {
  const shopsCollection = collection(db, Collections.ShopGroups).withConverter(converterOf(EntityShopGroup));

  const fetchAll = async (): Promise<EntityShopGroup[]> => {
    const response = await getDocs<EntityShopGroup>(shopsCollection);

    return response.docs.map((shopGroup) => shopGroup.data());
  };

  const fetchById = async (id: string): Promise<EntityShopGroup> => {
    const shopGroup = doc(shopsCollection, id);
    const resource = await getDoc<EntityShopGroup>(shopGroup);

    const data = resource.data();

    if (!data) {
      throw new FetchError({ trace: 'fetch', message: `Não foi possível carregar o grupo de loja id: ${id}}`});
    }

    return data;
  };

  const create = async (shopGroup: Omit<EntityShopGroup, 'id'>): Promise<EntityShopGroup> => {
    const createShopGroup = httpsCallable<unknown, EntityShopGroup>(functions, 'insertShopGroup');

    const newShopGroup = new CreateShopGroupInput(shopGroup);
    const { data } = await createShopGroup(instanceToPlain(newShopGroup));

    return plainToInstance(EntityShopGroup, data);
  };

  const update = async (shopGroup: Partial<EntityShopGroup> & { id: string }): Promise<void> => {
    const updateShopGroup = httpsCallable(functions, 'updateShopGroup');

    const instance = new UpdateShopGroupInput(shopGroup);

    await updateShopGroup(instanceToPlain(instance));
  };

  return { fetchAll, fetchById, create, update };
}
